/**
 * What the trigger form starts with: a fresh trigger, a fresh target, and a
 * stored record read back into form values.
 *
 * `triggerRecordToFormValues` is the inverse of `buildTriggerRequest`: a
 * page that reseeds a stored trigger even slightly differently would rewrite
 * it on the next save.
 */

import {
  type FileSelector,
  type InvocationInput,
  stringifyCommand,
} from "@/shared/domain/actions";
import {
  ACCEPT_ALL_PATTERN,
  browserTimeZone,
  DatasetBinding,
  entityOfSubscription,
  eventLabel,
  isEventSubscription,
  newScheduleDraft,
  OncePer,
  scheduleFromUtcCron,
  ThreadVisibility,
  TriggerSourceType,
  TriggerTargetType,
  type PlatformEventType,
  type TriggerTargetSpec,
  type TriggerV2Record,
} from "@/shared/domain/triggers";

import { conditionToValues, emptyConditionValues } from "./conditionValues";
import {
  defaultTargetKindFor,
  suppliedInputFor,
  TRIGGER_FORM_ENTITIES,
  type TriggerFormEntity,
} from "./formScope";
import { gateConditionFor, sharedGateOf } from "./gateCondition";
import {
  defaultInvocationSpecValues,
  TRIGGERING_DATASET_ID,
  type InputSourceValues,
  type InvocationSpecValues,
} from "./targets/invokeAction/invocationSpecSchema";
import {
  type EventSubscriptionValues,
  type ScheduleValues,
  type TargetFormValues,
  type TriggerFormValues,
} from "./triggerFormSchema";

/** A client-side id for a target the author just added. */
export function newTargetId(): string {
  return `tgt_${crypto.randomUUID().slice(0, 8)}`;
}

/** What a new target is born into: the trigger's source, for sensible defaults. */
export interface TargetDraftContext {
  events: readonly PlatformEventType[];
  firesOnSchedule: boolean;
}

/**
 * The message a Slack target starts with: the change and the subject's most
 * recognizable field, so a target added and saved as-is already says
 * something useful. Empty when there is no subject to name yet.
 */
export function defaultSlackMessage(context: TargetDraftContext): string {
  if (context.firesOnSchedule) {
    return "{{trigger.name}} ran on schedule at {{schedule.scheduled_for}}";
  }
  const entity = entityOfSubscription(context.events);
  if (entity === null) {
    return "";
  }
  const label = context.events.map(eventLabel).join(" / ");
  switch (entity) {
    case "file":
      return `${label}: {{file.relative_path}} in {{dataset.name}}`;
    case "dataset":
      return `${label}: {{dataset.name}} ({{dataset.dataset_id}})`;
    case "invocation":
      return `${label}: {{action.name}} ({{invocation.invocation_id}})`;
    case "event":
      return `${label}: {{event.name}} ({{event.event_id}})`;
    case "trigger":
      return label;
  }
}

/** A subscription nobody has checked a change on yet. */
export function defaultEventSubscriptionValues(): EventSubscriptionValues {
  return {
    type: TriggerSourceType.Events,
    entity: TRIGGER_FORM_ENTITIES[0],
    events: [],
    oncePer: OncePer.Occurrence,
  };
}

/** A schedule nobody has edited yet, stated in `timeZone`. */
export function defaultScheduleValues(
  timeZone: string = browserTimeZone(),
): ScheduleValues {
  return { type: TriggerSourceType.Schedule, ...newScheduleDraft(timeZone) };
}

/** A trigger nobody has said anything about yet, on one empty target. */
export function defaultTriggerFormValues(): TriggerFormValues {
  const firesOn = defaultEventSubscriptionValues();
  return {
    name: "",
    enabled: true,
    firesOn,
    condition: emptyConditionValues(),
    // Whatever that source offers, rather than a kind named here: the two
    // would drift, and a kind the source closes cannot be the one it starts on.
    targets: [newTargetValues(defaultTargetKindFor(firesOn))],
  };
}

/** A target of `type`, born into the trigger's source for sensible defaults. */
export function newTargetValues(
  type: TriggerTargetType,
  context: TargetDraftContext = { events: [], firesOnSchedule: false },
): TargetFormValues {
  const targetId = newTargetId();
  switch (type) {
    case TriggerTargetType.InvokeAction:
      return {
        type: TriggerTargetType.InvokeAction,
        targetId,
        spec: defaultInvocationSpecValues(),
        requiredInputs: [],
        requiredParameters: [],
        storedAdditionalInputs: [],
      };
    case TriggerTargetType.StartAgent:
      return {
        type: TriggerTargetType.StartAgent,
        targetId,
        agentId: null,
        variables: {},
        requiredVariables: [],
        visibility: ThreadVisibility.ORG,
        analysisScope: null,
      };
    case TriggerTargetType.SendSlackMessage:
      return {
        type: TriggerTargetType.SendSlackMessage,
        targetId,
        channelId: null,
        text: defaultSlackMessage(context),
      };
  }
}

/**
 * Seeds the form from a stored trigger.
 *
 * `timeZone` states the schedule's clock time; it defaults to the browser's,
 * which is the only zone a stored UTC cron can be read back into, and is a
 * parameter so a test can pin it.
 */
export function triggerRecordToFormValues(
  record: TriggerV2Record,
  timeZone: string = browserTimeZone(),
): TriggerFormValues {
  const source = record.fires_on;
  const firesOn: TriggerFormValues["firesOn"] = isEventSubscription(source)
    ? {
        type: TriggerSourceType.Events,
        entity: subscriptionEntity(source.events),
        events: source.events,
        oncePer: source.once_per,
      }
    : {
        type: TriggerSourceType.Schedule,
        ...scheduleFromUtcCron(source.cron, timeZone),
      };
  const supplied = suppliedInputFor(firesOn);
  const condition = conditionToValues(record.condition);
  // A file trigger's patterns are a gate, which is a condition's job. They move
  // across only when the move is exact and there is somewhere to put them: a
  // condition the cards cannot show is kept whole, so the gate stays put.
  const rows = condition.type === "rows" ? condition.rows : null;
  const gate =
    supplied === "changed-file" && rows !== null
      ? gateConditionFor(sharedGateOf(record.targets.map(storedGate)) ?? [])
      : null;
  return {
    name: record.name,
    enabled: record.enabled,
    firesOn,
    condition:
      gate === null || rows === null
        ? condition
        : { type: "rows", rows: [...rows, gate] },
    targets: record.targets.map((spec) =>
      targetSpecToFormValues(spec, supplied, gate !== null),
    ),
  };
}

/**
 * The entity the form's Select shows. A subscription spanning entities has
 * none of its own, and `isEditableInForm` turns those away before the form is
 * ever built, so the first offered entity is only a placeholder.
 */
function subscriptionEntity(
  events: readonly PlatformEventType[],
): TriggerFormEntity {
  const entity = entityOfSubscription(events);
  return (
    TRIGGER_FORM_ENTITIES.find((offered) => offered === entity) ??
    TRIGGER_FORM_ENTITIES[0]
  );
}

/**
 * One stored target read back into the values its card edits.
 *
 * A lone accept-all pattern is how the builder spells "every file", so it
 * reads back as no pattern and the round trip does not double it. Beside
 * other patterns it widens them, and is kept.
 */
export function targetSpecToFormValues(
  spec: TriggerTargetSpec,
  supplied: ReturnType<typeof suppliedInputFor> = null,
  gateMoved = false,
): TargetFormValues {
  switch (spec.type) {
    case TriggerTargetType.InvokeAction:
      return {
        type: TriggerTargetType.InvokeAction,
        targetId: spec.target_id,
        spec: invocationSpecValuesOf(spec, supplied),
        // A dataset firing's patterns were the mount, which the invocation
        // block has read back as an input row. A file firing's are a gate, and
        // are kept only while they are still the gate -- once one has moved to
        // a condition, the form sends the open list the runtime needs.
        requiredInputs:
          supplied === "changed-file" && !gateMoved ? storedGate(spec) : [],
        // Known once the action loads; the card notes them then.
        requiredParameters: [],
        storedAdditionalInputs: spec.additional_inputs ?? [],
      };
    case TriggerTargetType.StartAgent:
      return {
        type: TriggerTargetType.StartAgent,
        targetId: spec.target_id,
        agentId: spec.agent_id,
        variables: { ...spec.values },
        // Known once the agent loads; the card notes them then.
        requiredVariables: [],
        visibility: spec.visibility,
        analysisScope: spec.analysis_scope ?? null,
      };
    case TriggerTargetType.SendSlackMessage:
      return {
        type: TriggerTargetType.SendSlackMessage,
        targetId: spec.target_id,
        channelId: spec.channel_id,
        text: spec.text,
      };
  }
}

/**
 * The patterns a stored target gates on. A lone accept-all is how the builder
 * spells "every file", not something anyone typed, so it reads back as none.
 */
function storedGate(spec: TriggerTargetSpec): string[] {
  if (spec.type !== TriggerTargetType.InvokeAction) {
    return [];
  }
  return spec.required_inputs.length === 1 &&
    spec.required_inputs[0] === ACCEPT_ALL_PATTERN
    ? []
    : spec.required_inputs;
}

/** The shared invocation block a stored target holds. */
function invocationSpecValuesOf(
  spec: Extract<TriggerTargetSpec, { type: TriggerTargetType.InvokeAction }>,
  supplied: ReturnType<typeof suppliedInputFor>,
): InvocationSpecValues {
  const compute = spec.compute_requirement_overrides;
  const container = spec.container_parameter_overrides;
  const destination = spec.upload_destination ?? null;
  return {
    action: {
      name: spec.action.name,
      owner: spec.action.owner ?? null,
      digest: spec.action.digest ?? null,
    },
    parameters: { ...spec.parameter_values },
    // A dataset firing used to mount its files through `required_inputs`.
    // Those patterns are an input row now, so a trigger saved the old way
    // opens with the same files mounted and saves them the new way.
    inputs: [
      ...(supplied === "changed-dataset" && storedGate(spec).length > 0
        ? [{ source: "triggering-dataset" as const, globs: storedGate(spec) }]
        : []),
      ...inputRowsOf(spec.invocation_input),
    ],
    outputDestination:
      destination === null
        ? { mode: "source", datasetId: null }
        : { mode: "dataset", datasetId: destination.destination_id },
    overrides: {
      cpu: compute?.vCPU?.toString(),
      memory: compute?.memory?.toString(),
      gpu:
        compute?.gpu === undefined || compute.gpu === null
          ? undefined
          : String(compute.gpu),
      storage: compute?.storage ?? undefined,
      timeout: spec.timeout ?? undefined,
      workdir: container?.workdir ?? undefined,
      command: container?.command
        ? stringifyCommand(container.command)
        : undefined,
      entrypoint: container?.entry_point
        ? stringifyCommand(container.entry_point)
        : undefined,
      envVars: Object.entries(container?.env_vars ?? {}).map(
        ([key, value]) => ({ key, value }),
      ),
    },
  };
}

function isNonEmpty(values: readonly string[] | null): values is string[] {
  return values !== null && values.length > 0;
}

/** `value` as a RoboQL string literal, which escapes only `\` and `"`. */
function roboqlString(value: string): string {
  return `"${value.replace(/\\/g, "\\\\").replace(/"/g, '\\"')}"`;
}

/** A stored `invocation_input` read back as the rows that spell it. */
export function inputRowsOf(
  input: InvocationInput | null | undefined,
): InputSourceValues[] {
  if (input === null || input === undefined || input.files === null) {
    return [];
  }
  const selectors = Array.isArray(input.files) ? input.files : [input.files];
  return selectors.flatMap(selectorToRows);
}

/**
 * The rows one stored selector reads back as. The runtime looks up every
 * field a selector fills and gathers the files together, so a selector that
 * fills several is the same as one row for each.
 *
 * Files picked by id or by name have no row of their own and read back as the
 * query that finds them: for names, the very query the runtime builds. The
 * next save states them that way, which `query` is the preferred spelling of.
 */
function selectorToRows(selector: FileSelector): InputSourceValues[] {
  const rows: InputSourceValues[] = [];
  const paths = selector.paths ?? [];
  // An empty query is no query, which is how the runtime reads one.
  const query = selector.query === "" ? null : selector.query;
  const fillsAnotherField =
    query !== null || isNonEmpty(selector.ids) || isNonEmpty(selector.names);
  // The runtime reads a dataset only beside paths. On its own it is how the
  // form spells "every file"; beside another field it is ignored.
  if (
    selector.dataset_id !== null &&
    (paths.length > 0 || !fillsAnotherField)
  ) {
    rows.push(
      selector.dataset_id === TRIGGERING_DATASET_ID
        ? { source: "triggering-dataset", globs: paths }
        : { source: "dataset", datasetId: selector.dataset_id, globs: paths },
    );
  }
  if (query !== null) {
    rows.push(queryToRow(query));
  }
  if (isNonEmpty(selector.ids)) {
    const text = selector.ids
      .map((id) => `file_id = ${roboqlString(id)}`)
      .join(" OR ");
    rows.push({ source: "query", text });
  }
  if (isNonEmpty(selector.names)) {
    const text = selector.names
      .map((name) => `path LIKE ${roboqlString(name)}`)
      .join(" OR ");
    rows.push({ source: "query", text });
  }
  return rows;
}

// A stored query can name its dataset inside the query text. It is read back
// as a dataset row, so the next save states it as the selector's own fields.
function queryToRow(query: string): InputSourceValues {
  const binding = DatasetBinding.fromQuery(query);
  if (binding === null) {
    return { source: "query", text: query };
  }
  return binding.datasetId === TRIGGERING_DATASET_ID
    ? { source: "triggering-dataset", globs: [...binding.patterns] }
    : {
        source: "dataset",
        datasetId: binding.datasetId,
        globs: [...binding.patterns],
      };
}
